import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  Divider,
  FormControlLabel,
  Switch,
  FormGroup,
  Grid
} from '@mui/material';
import { fetchUserData } from '../services/healinApi';

/**
 * Test component for the User API integration
 */
const UserDataTest = () => {
  const [userId, setUserId] = useState('');
  const [apiKey, setApiKey] = useState(process.env.REACT_APP_HEALIN_API_KEY || '');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);

  // Custom fetch implementation for testing
  const testFetchUser = async () => {
    if (!userId.trim()) {
      setError('Please enter a User ID');
      return;
    }

    setLoading(true);
    setError(null);
    setUserData(null);
    setRawResponse(null);

    try {
      const url = `https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/users?User_ID=${encodeURIComponent(userId)}`;
      console.log('Direct fetch from URL:', url);
      console.log('Using API Key:', apiKey ? `${apiKey.substring(0, 5)}...` : 'Not set');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
      });

      const responseData = await response.text();
      console.log('Raw response:', responseData);
      
      let parsedData;
      try {
        parsedData = JSON.parse(responseData);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        parsedData = { rawText: responseData };
      }
      
      setRawResponse({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers.entries()]),
        data: parsedData
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }

      // Try to parse the response as JSON if we got here
      try {
        const jsonData = parsedData;
        setUserData(jsonData);
      } catch (e) {
        setError(`Response was not valid JSON: ${e.message}`);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Using our service function
  const handleFetchUser = async () => {
    if (!userId.trim()) {
      setError('Please enter a User ID');
      return;
    }

    setLoading(true);
    setError(null);
    setUserData(null);
    setRawResponse(null);

    try {
      const data = await fetchUserData(userId);
      setUserData(data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Healin API Test
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Test the connection to Healin backend services by fetching user data.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              variant="outlined"
              placeholder="Enter User ID"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              variant="outlined"
              type={showApiKey ? "text" : "password"}
              sx={{ mb: 2 }}
            />
            <FormGroup>
              <FormControlLabel 
                control={
                  <Switch 
                    checked={showApiKey} 
                    onChange={() => setShowApiKey(!showApiKey)} 
                  />
                } 
                label="Show API Key" 
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleFetchUser} 
            disabled={loading || !userId.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Fetch Using Service'}
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={testFetchUser} 
            disabled={loading || !userId.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Direct API Test'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {rawResponse && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Raw API Response
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Status: {rawResponse.status} {rawResponse.statusText}</Typography>
          </Box>
          <Typography variant="subtitle1">Headers:</Typography>
          <Box component="pre" sx={{ 
            p: 2, 
            backgroundColor: 'rgba(0,0,0,0.05)', 
            borderRadius: 1,
            overflowX: 'auto',
            mb: 2
          }}>
            {JSON.stringify(rawResponse.headers, null, 2)}
          </Box>
          <Typography variant="subtitle1">Response Body:</Typography>
          <Box component="pre" sx={{ 
            p: 2, 
            backgroundColor: 'rgba(0,0,0,0.05)', 
            borderRadius: 1,
            overflowX: 'auto'
          }}>
            {JSON.stringify(rawResponse.data, null, 2)}
          </Box>
        </Paper>
      )}

      {userData && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            User Data
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {Array.isArray(userData) ? (
            userData.map((user, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box component="pre" sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(0,0,0,0.05)', 
                    borderRadius: 1,
                    overflowX: 'auto'
                  }}>
                    {JSON.stringify(user, null, 2)}
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Box component="pre" sx={{ 
              p: 2, 
              backgroundColor: 'rgba(0,0,0,0.05)', 
              borderRadius: 1,
              overflowX: 'auto'
            }}>
              {JSON.stringify(userData, null, 2)}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default UserDataTest; 